import { useTranslation } from "react-i18next";
import AssignmentOverviewForm from "../../AssignmentGenerator/components/forms/AssignmentOverviewForm";
import {
  BaseAssignmentResponseDto,
  BaseQuestionDtoType,
} from "@packages/api/madlenAPI.schemas";
import { QuestionPropsQuestionType } from "@packages/api/madlenAPI.schemas";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import {
  getGetAssignmentFromTeacherQueryKey,
  getGetAssignmentStudentsQueryKey,
  useAssignAssignmentToStudentsNew,
  useGetAssignmentStudents,
} from "@packages/api/madlenAPI";
import { useAssignmentStudentsTableColumns } from "./AssignmentStudentsTableColumns";
import { useState } from "react";
import { ClassroomAndStudentSelector } from "./ClassroomAndStudentSelector";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@packages/contexts/ToastContext";
import { Button, Text } from "../../../../core";
import { GenericTable } from "@core/table";

const SingleAssignmentOverviewTab = ({
  assignment,
  isLoading,
  error,
  setShowSuccessModal,
}: {
  assignment: BaseAssignmentResponseDto;
  isLoading: boolean;
  error: Error | null;
  setShowSuccessModal: (show: boolean) => void;
}) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState<
    {
      classroomId: number;
      studentIds: number[];
    }[]
  >([]);

  const { data: students } = useGetAssignmentStudents(Number(assignment.id), {
    query: {
      refetchInterval: 10000,
    },
  });
  const table = useReactTable({
    data: students ?? [],
    columns: useAssignmentStudentsTableColumns(Number(assignment.id)),
    getCoreRowModel: getCoreRowModel(),
  });

  const assignedStudentIds = (
    students?.map((student) => student.studentId) ?? []
  ).filter((id): id is number => id !== undefined);

  const getAssignmentFromTeacherQueryKey = getGetAssignmentFromTeacherQueryKey(
    Number(assignment.id),
  );

  const getAssignmentStudentsQueryKey = getGetAssignmentStudentsQueryKey(
    Number(assignment.id),
  );

  const [isAssigning, setIsAssigning] = useState(false);

  const { mutate: assignAssignment } = useAssignAssignmentToStudentsNew({
    mutation: {
      onMutate: () => {
        setIsAssigning(true);
      },
      onError: () => {
        addToast({
          title: t("notifications.assignment.assignError"),
          variant: "error",
          duration: 2000,
        });
      },
      onSuccess: () => {
        setShowSuccessModal(true);

        queryClient.invalidateQueries({
          queryKey: getAssignmentFromTeacherQueryKey,
        });
        queryClient.invalidateQueries({
          queryKey: getAssignmentStudentsQueryKey,
        });
      },
      onSettled: () => {
        setIsAssigning(false);
      },
    },
  });

  const assignButton = (
    <Button
      size="md"
      rounded="lg"
      bgColor={
        selectedStudents.length > 0
          ? "bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600"
          : "bg-primary-500 hover:bg-primary-600"
      }
      textColor="text-white"
      className={`flex flex-1 sm:flex-initial justify-center items-center gap-2 group min-w-fit transition-all duration-300 ${
        selectedStudents.length > 0
          ? "shadow-md hover:shadow-lg scale-105 hover:scale-[1.07]"
          : ""
      }`}
      disabled={
        (selectedStudents.length === 0 && showAssignDialog) || isAssigning
      }
      onClick={() => {
        if (showAssignDialog) {
          assignAssignment(
            {
              assignmentId: assignment.id,
              data: { items: selectedStudents },
            },
            {
              onSettled: () => {
                setSelectedStudents([]);
                setShowAssignDialog(false);
              },
            },
          );
        } else {
          setShowAssignDialog(true);
          if (assignment.assignedStudentsWithClassrooms)
            setSelectedStudents(
              assignment.assignedStudentsWithClassrooms.map((s) => ({
                classroomId: s.classroomId!,
                studentIds: s.studentIds!,
              })),
            );
        }
      }}
    >
      {!isAssigning && selectedStudents.length === 0 && (
        <svg
          className="h-4 w-4 transition-transform duration-200 group-hover:scale-110"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m8-8H4"
          />
        </svg>
      )}
      {selectedStudents.length > 0 && (
        <svg
          className="h-4 w-4 animate-pulse"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      )}

      <span className="whitespace-nowrap">
        {isAssigning
          ? t("common.loading")
          : t("assignments.generator.steps.assignButton")}
      </span>
    </Button>
  );

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-6 mb-6">
          <Text variant="h3" className="text-xl sm:text-2xl">
            {t("assignments.table.title")}
          </Text>
          {assignedStudentIds.length === 0 && (
            <div className="flex gap-2 w-full sm:w-auto">{assignButton}</div>
          )}
        </div>
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          {showAssignDialog ? (
            <div className="min-w-full px-4 sm:px-0">
              <ClassroomAndStudentSelector
                selectedStudents={selectedStudents}
                setSelectedStudents={setSelectedStudents}
                assignedStudentIds={assignedStudentIds}
              />
            </div>
          ) : (
            <div className="min-w-full">
              <GenericTable
                table={table}
                isLoading={isLoading}
                error={error}
                isEmpty={!assignment.assignedStudentIds.length}
              />
            </div>
          )}
        </div>
        {assignedStudentIds.length !== 0 && (
          <div className="flex ml-auto mt-4 gap-2 w-full sm:w-auto">
            {assignButton}
          </div>
        )}
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-6">
        <AssignmentOverviewForm
          hideGeneralInfo={true}
          assignmentData={{
            ...assignment,
            assignmentDifficulty: assignment.difficulty ?? "EASY",
            duration: assignment.duration ?? 0,
            startDate: assignment.startDate ?? "",
            endDate: assignment.endDate ?? "",
            grade: assignment.grade ?? 0,
            generator: "PROMPT",
            hwAssignmentType: assignment.type,
            questionTypes: Object.entries(
              assignment.questions.reduce(
                (acc, question) => {
                  acc[question.type] = {
                    count: (acc[question.type]?.count || 0) + 1,
                    point: (acc[question.type]?.point || 0) + question.point,
                  };
                  return acc;
                },
                {} as Record<
                  BaseQuestionDtoType,
                  { count: number; point: number }
                >,
              ),
            ).map((question) => ({
              count: question[1].count,
              difficulty: "EASY",
              point: question[1].point / question[1].count,
              questionType: question[0] as QuestionPropsQuestionType,
            })),
          }}
        />
      </div>
    </div>
  );
};

export default SingleAssignmentOverviewTab;
